/**
 * AccessGuards - Route Guard Components using useAccess()
 * 
 * These components wrap protected routes and enforce access control at the UI layer.
 * IMPORTANT: These guards provide UX guidance only. The real enforcement happens
 * at the Database (RLS) and Server (edge functions) layers.
 * 
 * Components:
 * - RequireVerifiedEmail: Requires authenticated + email verified + not suspended
 * - RequireSubscriber: Requires verified email + active subscription + not suspended
 * - AccountSuspendedPage: Block page shown to suspended users
 */

import { ReactNode } from 'react';
import { Navigate, useLocation, Link } from 'react-router-dom';
import { useAccess } from '@/hooks/useAccess';
import { Loader2, ShieldX, Home, ArrowLeft, LogOut, Mail, Crown, Lock, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Loading spinner component for auth checks
 */
function AccessLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

/**
 * Account Suspended Block Page
 * Shown when user's account status is 'suspended' or 'deleted_requested'
 * Allows logout and support access only
 */
export function AccountSuspendedPage() {
  const { signOut } = useAuth();
  const { accountStatus } = useAccess();
  
  const isDeletionRequested = accountStatus === 'deleted_requested';
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertTriangle className="h-8 w-8 text-destructive" />
          </div>
          <CardTitle className="text-2xl">
            {isDeletionRequested ? 'Account Pending Deletion' : 'Account Suspended'}
          </CardTitle>
          <CardDescription className="text-base">
            {isDeletionRequested 
              ? 'Your account is scheduled for deletion.'
              : 'Your account has been suspended.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground text-center">
            {isDeletionRequested 
              ? 'If you wish to cancel the deletion and restore your account, please contact support.'
              : 'If you believe this is an error, please contact our support team for assistance.'}
          </p>
          
          <div className="flex flex-col gap-3 pt-4">
            <Button asChild variant="outline" className="w-full">
              <a href="mailto:support@autonewsweekly.com">
                <Mail className="h-4 w-4 mr-2" />
                Contact Support
              </a>
            </Button>
            <Button 
              variant="ghost" 
              className="w-full"
              onClick={() => signOut()}
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Email Verification Required Page
 * Shown when user is authenticated but email is not verified
 */
function EmailVerificationRequiredPage() {
  const { signOut } = useAuth();
  const { userEmail } = useAccess();
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-amber-500/10 flex items-center justify-center">
            <Mail className="h-8 w-8 text-amber-500" />
          </div>
          <CardTitle className="text-2xl">Verify Your Email</CardTitle>
          <CardDescription className="text-base">
            Please verify your email address to continue
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground text-center">
            We sent a verification link to <strong>{userEmail}</strong>. 
            Please check your inbox and click the link to verify your account.
          </p>
          
          <div className="flex flex-col gap-3 pt-4">
            <Button asChild className="w-full">
              <Link to="/auth/verify">
                <Mail className="h-4 w-4 mr-2" />
                Resend Verification Email
              </Link>
            </Button>
            <Button 
              variant="ghost" 
              className="w-full"
              onClick={() => signOut()}
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
          
          <p className="text-xs text-muted-foreground text-center pt-4">
            Didn't receive the email? Check your spam folder or{' '}
            <a href="mailto:support@autonewsweekly.com" className="text-primary hover:underline">
              contact support
            </a>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Subscription Required - Redirect to /upgrade
 * Non-subscribers are redirected to the upgrade page with proper UX
 */
function SubscriptionRequiredRedirect() {
  return <Navigate to="/upgrade" replace />;
}

/**
 * Subscription Needs Attention Page
 * Shown when user has subscription but it's past_due, canceled, etc.
 */
function SubscriptionNeedsAttentionPage() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center">
            <Lock className="h-8 w-8 text-destructive" />
          </div>
          <CardTitle className="text-2xl">Subscription Inactive</CardTitle>
          <CardDescription className="text-base">
            Your subscription needs attention to restore access
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground text-center">
            There may be an issue with your payment method. Please update your 
            billing information to continue accessing premium content.
          </p>
          
          <div className="flex flex-col gap-3 pt-4">
            <Button asChild className="w-full">
              <Link to="/account?tab=account">
                Manage Subscription
              </Link>
            </Button>
            <Button asChild variant="outline" className="w-full">
              <Link to="/insights">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Insights
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

interface GuardProps {
  children: ReactNode;
}

/**
 * RequireVerifiedEmail - Route Guard
 * 
 * Enforces: authenticated + email verified + not suspended
 * 
 * Redirect targets:
 * - Not authenticated → /auth/buyer with returnTo
 * - Suspended → AccountSuspendedPage (no redirect)
 * - Not verified → EmailVerificationRequiredPage (no redirect, shows CTA)
 */
export function RequireVerifiedEmail({ children }: GuardProps) {
  const { isAuthenticated, isEmailVerified, isSuspended, loading } = useAccess();
  const location = useLocation();

  if (loading) {
    return <AccessLoader />;
  }

  // Not authenticated → redirect to login
  if (!isAuthenticated) {
    const returnTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth/buyer?returnTo=${returnTo}`} replace />;
  }

  // Suspended → show block page (before verification check)
  if (isSuspended) {
    return <AccountSuspendedPage />;
  }

  // Not verified → show verification page
  if (!isEmailVerified) {
    return <EmailVerificationRequiredPage />;
  }

  return <>{children}</>;
}

/**
 * RequireSubscriber - Route Guard
 * 
 * Enforces: authenticated + email verified + active subscription + not suspended
 * 
 * Redirect targets:
 * - Not authenticated → /auth/buyer with returnTo
 * - Suspended → AccountSuspendedPage
 * - Not verified → EmailVerificationRequiredPage
 * - No subscription → SubscriptionRequiredPage
 * - Subscription needs attention → SubscriptionNeedsAttentionPage
 */
export function RequireSubscriber({ children }: GuardProps) {
  const { 
    isAuthenticated, 
    isEmailVerified, 
    isSubscriber, 
    isSuspended,
    accountStatus,
    loading,
  } = useAccess();
  const location = useLocation();

  if (loading) {
    return <AccessLoader />;
  }

  // Not authenticated → redirect to login
  if (!isAuthenticated) {
    const returnTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth/buyer?returnTo=${returnTo}&intent=subscribe`} replace />;
  }

  // Suspended → show block page (highest priority after auth)
  if (isSuspended) {
    return <AccountSuspendedPage />;
  }

  // Not verified → show verification page
  if (!isEmailVerified) {
    return <EmailVerificationRequiredPage />;
  }

  // Not a subscriber → redirect to upgrade page
  if (!isSubscriber) {
    return <SubscriptionRequiredRedirect />;
  }

  return <>{children}</>;
}
